import React, { useCallback, useEffect, useState } from "react";
import { Camera, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

function Result({ s }) {
  const [open, setOpen] = useState(false);
  if (!s.finished_at) return null;
  const failed = s.failed || [];
  const ok = Math.max(0, (s.done || 0) - failed.length);
  if (s.state === "error") {
    return (
      <div className="text-xs mt-2 text-red-700" data-testid="guide-shots-result">
        Stopped early {fmt(s.finished_at)} after {ok} pictures: {s.error}
      </div>
    );
  }
  return (
    <div className="text-xs mt-2" data-testid="guide-shots-result">
      Last retake {fmt(s.finished_at)}: {ok} updated
      {failed.length > 0 && (
        <>
          , {failed.length} couldn't be taken (the old picture stays).{" "}
          <button className="underline" onClick={() => setOpen(!open)} data-testid="guide-shots-failed-toggle">{open ? "Hide" : "Show"}</button>
          {open && (
            <ul className="mt-1 list-disc pl-5 font-mono" data-testid="guide-shots-failed-list">
              {failed.map((f) => <li key={f.name} title={f.detail || ""}>{f.name} — {f.error}</li>)}
            </ul>
          )}
        </>
      )}
      {(s.skipped || []).length > 0 && <div className="mt-1 text-zinc-600">No test account for: {s.skipped.join(", ")} — those pictures were kept.</div>}
    </div>
  );
}

function Outdated({ names, onRetake, canRetake }) {
  const [open, setOpen] = useState(false);
  if (!names?.length) return <div className="text-xs mt-2 text-emerald-700" data-testid="guide-shots-outdated-none">All pictures match their screens.</div>;
  return (
    <div className="text-xs mt-2 border border-amber-600 bg-amber-50 p-2" data-testid="guide-shots-outdated">
      <span data-testid="guide-shots-outdated-count">{names.length} {names.length === 1 ? "picture" : "pictures"} may be outdated</span> — the screen changed after it was taken.{" "}
      <button className="underline" onClick={() => setOpen(!open)} data-testid="guide-shots-outdated-toggle">{open ? "Hide" : "Show"}</button>
      {canRetake && (
        <button onClick={onRetake} data-testid="guide-shots-retake-outdated-btn"
          className="ml-2 border border-black bg-white px-2 py-0.5 uppercase tracking-widest text-[10px] hover:bg-black hover:text-white transition-colors duration-100 disabled:opacity-50">
          Retake outdated only
        </button>
      )}
      {open && <ul className="mt-1 list-disc pl-5 font-mono" data-testid="guide-shots-outdated-list">{names.map((n) => <li key={n}>{n}</li>)}</ul>}
    </div>
  );
}

export function GuideScreenshotsPanel() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const load = useCallback(() => api.get("/help/screenshots/status").then((r) => setS(r.data)).catch(() => setS(null)), []);
  useEffect(() => { if (user?.role === "super_admin") { load(); } }, [user?.role, load]);
  useEffect(() => {
    if (!s?.running) { return undefined; }
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [s?.running, load]);
  if (user?.role !== "super_admin" || !s) return null;
  const start = (onlyOutdated) => api.post("/help/screenshots/retake", { only_outdated: Boolean(onlyOutdated) })
    .then(() => { toast.success(onlyOutdated ? "Retaking the outdated pictures" : "Retaking screenshots — this takes about 5 minutes"); load(); })
    .catch((e) => toast.error(e.response?.data?.detail || "Couldn't start"));
  return (
    <div className="brut-card p-4 mb-6 bg-white" data-testid="guide-shots-panel">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <div className="label-mono">Guide screenshots</div>
          <div className="text-sm" data-testid="guide-shots-summary">{s.pictures} pictures · oldest taken {fmt(s.oldest)}</div>
        </div>
        {s.available && <button onClick={() => start(false)} disabled={s.running} data-testid="guide-shots-retake-btn"
          className="border border-black bg-black text-white hover:bg-yellow-300 hover:text-black transition-colors duration-100 inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-50 disabled:hover:bg-black disabled:hover:text-white">
          {s.running ? <CircleNotch size={14} className="animate-spin" /> : <Camera size={14} weight="bold" />}
          {s.running ? "Retaking…" : "Retake screenshots"}
        </button>}
      </div>
      {s.running && (
        <div className="mt-3" data-testid="guide-shots-progress">
          <div className="h-2 border border-black bg-zinc-100"><div className="h-full bg-black transition-[width] duration-500" style={{ width: `${s.total ? (100 * s.done) / s.total : 0}%` }} /></div>
          <div className="text-xs mt-1 font-mono">{s.done} of {s.total}{s.current ? ` · ${s.current}` : ""}</div>
        </div>
      )}
      {!s.available && <div className="text-xs mt-2 text-zinc-600" data-testid="guide-shots-unavailable">Pictures can't be retaken on the live site. Your developer retakes them on the test copy, and the new pictures arrive with your next publish.</div>}
      {!s.running && <Outdated names={s.outdated} onRetake={() => start(true)} canRetake={s.available} />}
      {!s.running && <Result s={s} />}
    </div>
  );
}
